import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { fetchTransportCompanyById, fetchListingsByOwner } from '../api/services';
import { useFetch } from '../hooks';
import {
  LoadingSpinner,
  ErrorDisplay,
  VerifiedBadge,
  EmptyState,
  ListingCard,
  LocationMap,
  ImageLightbox,
  ContactItem,
} from '../components/common';
import { DESIGN_TOKENS } from '../styles/theme';
import { resolveAssetUrl } from '../utils';

export default function TransportCompanyDetailPage() {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const [lightboxImages, setLightboxImages] = React.useState<{ url: string; name?: string }[]>([]);

  const { companyId } = useParams<{ companyId: string }>();
  const numericCompanyId = companyId ?? "";
  const {
    data: companyData,
    isLoading: isCompanyLoading,
    error: companyError,
    refetch,
  } = useFetch(
    () => fetchTransportCompanyById(numericCompanyId),
    [numericCompanyId],
    { enabled: numericCompanyId != undefined }
  );

  const company = companyData?.data;
  const ownerDocumentId = company?.documentId;

  const {
    data: listingsData,
    isLoading: isListingsLoading,
  } = useFetch(
    () => fetchListingsByOwner(ownerDocumentId!),
    [ownerDocumentId],
    { enabled: Boolean(ownerDocumentId) }
  );

  if (isCompanyLoading) {
    return <LoadingSpinner message="Ładowanie profilu firmy..." fullHeight />;
  }

  if (companyError || !company) {
    return (
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <ErrorDisplay message={companyError ?? 'Nie znaleziono firmy.'} onRetry={refetch} />
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button component={RouterLink} to="/companies/transport-companies" startIcon={<ArrowBackIcon />} variant="outlined">
            Powrót do katalogu
          </Button>
        </Box>
      </Container>
    );
  }

  const { company_information } = company;
  const { brand_graphics, company_data, contact_details, tax_information, verified, highlighted } = company_information;

  const logoUrl = resolveAssetUrl(brand_graphics?.logo?.url);
  const coverUrl = resolveAssetUrl(brand_graphics?.coverImage?.url);

  const openLightbox = (images: { url: string; name?: string }[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '60vh' }}>
      {/* Cover Image */}
      <Box
        sx={{
          height: { xs: 180, md: 260 },
          backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
          backgroundImage: coverUrl ? `url(${coverUrl})` : `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.primaryLight}40)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 60%)' }} />
      </Box>

      <Container maxWidth="xl">
        {/* Back button */}
        <Box sx={{ pt: 3, mb: 3 }}>
          <Button component={RouterLink} to="/companies/transport-companies" startIcon={<ArrowBackIcon />} variant="text" size="small">
            Powrót do katalogu firm
          </Button>
        </Box>

        <Grid container spacing={4}>
          {/* LEFT: Company Info Panel */}
          <Grid item xs={12} md={4} lg={3}>
            <Card sx={{ position: { md: 'sticky' }, top: 80, zIndex: 10 }}>
              <CardContent sx={{ p: 3 }}>
                {/* Logo */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '16px',
                    border: `3px solid ${DESIGN_TOKENS.colors.border}`,
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    boxShadow: DESIGN_TOKENS.shadows.card,
                  }}
                >
                  {logoUrl ? (
                    <Box component="img" src={logoUrl} alt="Logo firmy" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <BusinessIcon sx={{ fontSize: 40, color: DESIGN_TOKENS.colors.primaryLight }} />
                  )}
                </Box>

                {/* Company name */}
                <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                  {company_data?.company_name ?? 'Firma transportowa'}
                </Typography>

                {/* Badges */}
                <Box sx={{ mb: 2 }}>
                  <VerifiedBadge isVerified={verified ?? false} isHighlighted={highlighted ?? false} />
                </Box>

                {/* Contact details */}
                {contact_details?.email && (
                  <ContactItem
                    icon={<EmailIcon fontSize="small" color="primary" />}
                    label="E-mail"
                    value={contact_details.email}
                    href={`mailto:${contact_details.email}`}
                  />
                )}
                {contact_details?.phone_number && (
                  <ContactItem
                    icon={<PhoneIcon fontSize="small" color="primary" />}
                    label="Telefon"
                    value={contact_details.phone_number}
                    href={`tel:${contact_details.phone_number}`}
                  />
                )}
                {contact_details?.website_url && (
                  <ContactItem
                    icon={<LanguageIcon fontSize="small" color="primary" />}
                    label="Strona WWW"
                    value={contact_details.website_url.replace(/^https?:\/\//, '')}
                    href={contact_details.website_url}
                  />
                )}

                <Divider sx={{ my: 2 }} />

                {/* Tax information */}
                {tax_information?.tax_identification_number && (
                  <ContactItem
                    icon={<ReceiptIcon fontSize="small" color="primary" />}
                    label="NIP"
                    value={tax_information.tax_identification_number}
                  />
                )}
                {tax_information?.regon && (
                  <ContactItem
                    icon={<ReceiptIcon fontSize="small" color="primary" sx={{ opacity: 0 }} />}
                    label="REGON"
                    value={tax_information.regon}
                  />
                )}

                <Divider sx={{ my: 2 }} />

                {/* Location information */}
                {contact_details?.location && (
                  <>
                    <ContactItem
                      icon={<LocationOnIcon fontSize="small" color="primary" />}
                      label="Lokalizacja"
                      value={[contact_details.location.city, contact_details.location.country].filter(Boolean).join(', ') || 'Polska'}
                    />
                    <Box sx={{ height: 200, mb: 3, borderRadius: '8px', overflow: 'hidden', border: `1px solid ${DESIGN_TOKENS.colors.border}` }}>
                      <LocationMap location={contact_details.location} />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

          </Grid>

          {/* RIGHT: Content */}
          <Grid item xs={12} md={8} lg={9}>
            {company_data?.description && (
              <Box sx={{ mb: 5 }}>
                <Typography variant="h3" sx={{ mb: 2 }}>
                  O firmie
                </Typography>
                <Card variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                      {company_data.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Listings Section */}
            <Typography variant="h3" sx={{ mb: 3 }}>
              Ogłoszenia firmy
            </Typography>

            {isListingsLoading && <LoadingSpinner message="Ładowanie ogłoszeń..." />}

            {!isListingsLoading && listingsData?.data && listingsData.data.length > 0 ? (
              <Grid container spacing={2.5} sx={{ mb: 6 }}>
                {listingsData.data.map((listing) => (
                  <Grid item xs={12} sm={6} key={listing.id}>
                    <ListingCard listing={listing} />
                  </Grid>
                ))}
              </Grid>
            ) : !isListingsLoading ? (
              <Box sx={{ mb: 6 }}>
                <EmptyState
                  title="Brak ogłoszeń"
                  description="Ta firma nie opublikowała jeszcze żadnych ogłoszeń."
                />
              </Box>
            ) : null}

            {/* Certificates section */}
            {company_data?.certificates && company_data.certificates.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography variant="h3" sx={{ mb: 3 }}>Certyfikaty</Typography>
                <Grid container spacing={2}>
                  {company_data.certificates.map((certificate, index) => {
                    const certUrl = resolveAssetUrl(certificate.url) || '';
                    const certs = company_data.certificates!.map(c => ({ url: resolveAssetUrl(c.url) || '', name: c.name }));
                    return (
                      <Grid item xs={12} sm={6} md={4} key={certificate.id}>
                        <Card variant="outlined" sx={{ height: '100%', cursor: 'pointer', '&:hover': { borderColor: DESIGN_TOKENS.colors.primary } }} onClick={() => openLightbox(certs, index)}>
                          <Box sx={{ height: 160, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            <Box component="img" src={certUrl} alt={certificate.name} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          </Box>
                          <CardContent sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} noWrap title={certificate.name}>
                              {certificate.name}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Gallery section */}
            {brand_graphics?.gallery && brand_graphics.gallery.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h3" sx={{ mb: 3 }}>Galeria</Typography>
                <Grid container spacing={2}>
                  {brand_graphics.gallery.map((galleryImage, index) => {
                    const galleryImages = brand_graphics.gallery!.map(img => ({ url: resolveAssetUrl(img.url) || '', name: img.name }));
                    return (
                      <Grid item xs={6} sm={4} md={3} key={galleryImage.id}>
                        <Box
                          onClick={() => openLightbox(galleryImages, index)}
                          sx={{
                            display: 'block',
                            aspectRatio: '1/1',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                          }}
                        >
                          <Box
                            component="img"
                            src={resolveAssetUrl(galleryImage.url)}
                            alt={galleryImage.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.05)' } }}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Grid>
        </Grid>

        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      </Container>
    </Box>
  );
}
